<template>
    <div class="w-full">
        <!-- Artist Header -->
        <div class="w-full bg-gradient-to-r from-nomad-800 to-nomad-900 rounded-lg overflow-hidden mb-8">
            <div class="flex flex-col md:flex-row items-center gap-8 p-6 md:p-8">
                <!-- Artist Image -->
                <div class="flex-shrink-0">
                    <img :src="artist.image" class="h-40 w-40 md:h-56 md:w-56 object-cover rounded-lg shadow-lg"
                        :alt="artist.name" :title="artist.name" />
                </div>

                <!-- Artist Info -->
                <div class="flex-1 text-center md:text-left">
                    <span class="inline-block text-nomad-300 text-xs uppercase tracking-wide font-medium mb-3">
                        {{ artist.content_type }}
                    </span>
                    <h1 class="text-4xl md:text-5xl font-bold text-white mb-3">{{ artist.name }}</h1>
                    <p class="text-nomad-200 text-lg">
                        {{ artist.songs.length }} {{ artist.songs.length === 1 ? 'song' : 'songs' }}
                    </p>
                </div>
            </div>
        </div>

        <!-- Content Grid -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <!-- Biography Section -->
            <div v-if="artist.biography" class="lg:col-span-1">
                <div class="bg-nomad-800 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300">
                    <div class="p-6 space-y-4">
                        <h3 class="text-xl font-bold text-white">About</h3>
                        <div :class="{
                            'max-h-64 overflow-hidden relative': !expanded,
                            '': expanded,
                        }">
                            <div v-html="artist.biography"
                                class="wysiwyg font-light text-md text-nomad-200 leading-relaxed"></div>
                            <div class="bg-gradient-to-t from-nomad-800 absolute h-16 w-full bottom-0" v-if="!expanded">
                            </div>
                        </div>
                        <button @click="expandBio"
                            class="w-full flex items-center justify-center gap-2 text-nomad-accent hover:text-nomad-accent/80 transition-colors text-sm font-medium">
                            <span>{{ expanded ? 'Show less' : 'Show more' }}</span>
                            <span :class="{ 'icon-arrow-up': expanded, 'icon-arrow-down': !expanded }"></span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Main Content -->
            <div class="lg:col-span-2 space-y-8">
                <!-- Songs Section -->
                <div v-if="artist.songs.length">
                    <h2 class="text-2xl font-bold text-white mb-4">Songs</h2>
                    <div class="space-y-3">
                        <card-song v-for="song in artist.songs" :key="song.id" :song="song" />
                    </div>
                </div>

                <!-- Appears On Section -->
                <div v-if="artist.appears.length">
                    <h2 class="text-2xl font-bold text-white mb-4">Appears on</h2>
                    <div class="space-y-3">
                        <card-song v-for="song in artist.appears" :key="song.id" :song="song" />
                    </div>
                </div>

                <!-- Albums Section -->
                <div v-if="artist.albums.length">
                    <h2 class="text-2xl font-bold text-white mb-4">Albums</h2>
                    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        <album-card :album="album" v-for="album in artist.albums" :key="album.id" />
                    </div>
                </div>

                <!-- Events Section -->
                <div v-if="artist.events.length">
                    <h2 class="text-2xl font-bold text-white mb-4">Events</h2>
                    <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div :key="event.id" v-for="event in artist.events"
                            :class="{ 'opacity-50': !event.is_upcoming }">
                            <event-card :event="event" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
defineProps({ artist: Object });
</script>

<script>
import CardSong from "@/components/CardSong.vue";
import AlbumCard from "@/components/AlbumCard.vue";
import EventCard from '@/components/EventCard.vue';
import { defineAsyncComponent } from 'vue'
import { usePage } from "@inertiajs/vue3";
const page = usePage();
export default {
    inject: ["current"],
    provide() {
        return {
            stream: this.$root.$data,
        };
    },
    data() {
        return {
            expanded: false,
        };
    },
    components: {
        CardSong,
        AlbumCard,
        EventCard,
        'adsense': defineAsyncComponent(() => import("@/components/Adsense.vue")),
    },
    methods: {
        expandBio() {
            this.expanded = !this.expanded;
        },
    },
    beforeMount() {
        document.title = page.props.title
    }
};
</script>
